// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Deterministic demo fixtures. These are the approved copy strings from the PRD
// and the seed thread the drawer opens with. No real patient/encounter data.

import type { EliaContext, EliaMessage, EliaMessageCard } from './elia-chat-types';

/** Starter prompts for a general (no-patient) conversation. */
export const ELIA_GENERAL_SUGGESTIONS = [
  'Explain a possible diagnosis',
  'What are the red flags for a headache?',
  'Suggest questions to ask a patient',
  'Summarize a condition for me',
];

/** Fixed timestamp for the seed greeting so fixtures stay deterministic. */
export const ELIA_EPOCH = '2024-01-01T09:00:00.000Z';

export const ELIA_DEMO_CONTEXT: EliaContext = {
  patientName: 'Jessica Wong',
  contextLabel: "Today's consultation",
  userGreetingName: 'Dr. Smith',
};

export const ELIA_GREETING = "Hi Dr. Smith. How can I help with Jessica's consultation?";

export const ELIA_INITIAL_SUGGESTIONS = [
  'What should I check next?',
  'What information is missing?',
  'Explain the possible diagnosis',
  'Summarize this consultation',
];

export const ELIA_FOLLOWUP_SUGGESTIONS = [
  'Why?',
  'What are the red flags?',
  'What should I ask the patient?',
];

export const ELIA_HEADACHE_RESPONSE =
  'For a headache lasting three days, first confirm the severity, location, ' +
  'onset pattern, blood pressure, visual changes, nausea, weakness or numbness, ' +
  'fever, neck stiffness, and any recent head injury. The available record does ' +
  'not yet include all of these findings.';

export const ELIA_CHECKLIST_CARD: EliaMessageCard = {
  type: 'checklist',
  title: 'Things to check',
  items: [
    { id: 'blood-pressure', label: 'Blood pressure' },
    { id: 'sudden-onset', label: 'Sudden or severe onset' },
    { id: 'visual-changes', label: 'Visual changes' },
    { id: 'weakness-numbness', label: 'Weakness or numbness' },
    { id: 'fever-neck', label: 'Fever or neck stiffness' },
    { id: 'head-injury', label: 'Recent head injury' },
  ],
  informationUsed: 'Information used: Current consultation and available patient record',
  actionLabel: 'Add as checklist',
};

/** Copy shown when the (mocked) response fails. */
export const ELIA_FAILURE_MESSAGE = "I couldn't answer that right now. Please try again.";

/** The thread Elia opens with: a friendly greeting plus starter suggestions. */
export function createInitialThread(): EliaMessage[] {
  return [
    {
      id: 'elia-greeting',
      role: 'assistant',
      content: ELIA_GREETING,
      createdAt: ELIA_EPOCH,
      status: 'sent',
      suggestions: ELIA_INITIAL_SUGGESTIONS,
    },
  ];
}

/** Context-aware opening greeting (PRD.276 FR-8; scope-specific in PRD.278). */
export function greetingFor(context: EliaContext | undefined): string {
  const hi = context?.userGreetingName ? `Hi ${context.userGreetingName}.` : 'Hi.';
  if (context?.patientName) {
    const first = context.patientName.split(' ')[0];
    return context?.visitId
      ? `${hi} How can I help with ${first}'s current visit?`
      : `${hi} How can I help with ${first}'s care?`;
  }
  return `${hi} How can I help?`;
}

// Hidden for now: the live Doctor Consult response returns no follow-up
// suggestions, so the canned starter chips were the only chips left. Flip this
// back to `true` to restore the greeting's starter suggestions.
export const ELIA_SHOW_INITIAL_SUGGESTIONS = false;

/** Starter suggestions depend on whether a patient is in context. */
export function initialSuggestionsFor(context: EliaContext | undefined): string[] {
  if (!ELIA_SHOW_INITIAL_SUGGESTIONS) return [];
  return context?.patientName ? ELIA_INITIAL_SUGGESTIONS : ELIA_GENERAL_SUGGESTIONS;
}

/** Seed thread for a given context; `key` keeps the greeting id unique. */
export function createInitialThreadFor(
  context: EliaContext | undefined,
  key: string,
): EliaMessage[] {
  return [
    {
      id: `elia-greeting-${key}`,
      role: 'assistant',
      content: greetingFor(context),
      createdAt: ELIA_EPOCH,
      status: 'sent',
      suggestions: initialSuggestionsFor(context),
    },
  ];
}

// Monotonic id source. Avoids Date/Math.random so ids are stable and testable.
let idCounter = 0;

export function createEliaId(prefix = 'elia-msg'): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}
